import API_BASE_URL from 'src/constants';
import { getToken, handleUnauthorized } from './authToken';

export interface ChangelogAttachment {
  id: number;
  name: string;
  mimetype: string;
  size: number;
  is_image: boolean;
  is_video: boolean;
  url: string;
}

export interface ChangelogEntry {
  id: number;
  version: string | null;
  title: string;
  body_html: string;
  publish_date: string | null;
  video_url: string | null;
  video_embed_url: string | null;
  attachments: ChangelogAttachment[];
  seen: boolean;
}

interface PageDto<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages?: number;
}

const API_ORIGIN = (API_BASE_URL ?? '').replace(/\/$/, '');

/** Turn a backend-relative path such as `/portal/changelog/1/attachment/5` into an absolute URL. */
export const changelogAssetUrl = (path: string): string => (path.startsWith('/') ? `${API_ORIGIN}${path}` : path);

const request = async (path: string, init: RequestInit = {}): Promise<Response> => {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}portal/changelog${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (response.status === 401) handleUnauthorized(token);
  if (!response.ok) throw new Error(`Changelog request failed (${response.status})`);
  return response;
};

export async function fetchChangelogPage(page = 1, size = 20): Promise<PageDto<ChangelogEntry>> {
  return (await (await request(`?page=${page}&size=${size}`)).json()) as PageDto<ChangelogEntry>;
}

export async function fetchUnseenChangelog(): Promise<ChangelogEntry[]> {
  return (await (await request('/unseen')).json()) as ChangelogEntry[];
}

export async function fetchChangelogEntry(id: number): Promise<ChangelogEntry> {
  return (await (await request(`/${id}`)).json()) as ChangelogEntry;
}

export async function markChangelogSeen(id: number): Promise<void> {
  await request(`/${id}/seen`, { method: 'POST' });
}

/** Fetch a protected changelog asset (image, video, file) with the bearer token and return an object URL. */
export async function fetchChangelogAssetUrl(path: string, signal?: AbortSignal): Promise<string> {
  const token = getToken();
  const response = await fetch(changelogAssetUrl(path), { headers: { Authorization: `Bearer ${token}` }, signal });
  if (response.status === 401) handleUnauthorized(token);
  if (!response.ok) throw new Error(`Asset request failed (${response.status})`);
  return URL.createObjectURL(await response.blob());
}

export async function downloadChangelogAttachment(attachment: ChangelogAttachment): Promise<void> {
  const objectUrl = await fetchChangelogAssetUrl(attachment.url);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = attachment.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
